/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================
*/

import { useEffect, useMemo, useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import fetchWithAuth from "layouts/tables/data/api";
import appSettings from "appsettings.json";

function Organization() {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [error, setError] = useState(null);

  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openStatusConfirm, setOpenStatusConfirm] = useState(false);
  const [selectedStatusOrg, setSelectedStatusOrg] = useState(null);

  const defaultOrg = {
    id: 0,
    name: "",
    description: "",
    email: "",
    phoneNumber: "",
    address: "",
    city: "",
    region: "",
    postalCode: "",
    country: "",
    isActive: true,
  };

  const [createItem, setCreateItem] = useState(defaultOrg);
  const [editItem, setEditItem] = useState(defaultOrg);

  const normalizeArray = (value) => {
    if (Array.isArray(value)) return value;
    if (value && Array.isArray(value.$values)) return value.$values;
    return [];
  };

  const parseBool = (value) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      return normalized === "true" || normalized === "1" || normalized === "active";
    }
    return false;
  };

  const getId = (item) => item?.id ?? item?.Id ?? item?.organizationId ?? item?.OrganizationId ?? 0;
  const getName = (item) => item?.name ?? item?.Name ?? "";
  const getDescription = (item) => item?.description ?? item?.Description ?? "";
  const getEmail = (item) => item?.email ?? item?.Email ?? "";
  const getPhoneNumber = (item) => item?.phoneNumber ?? item?.PhoneNumber ?? "";
  const getAddress = (item) => item?.address ?? item?.Address ?? "";
  const getCity = (item) => item?.city ?? item?.City ?? "";
  const getRegion = (item) => item?.region ?? item?.Region ?? "";
  const getPostalCode = (item) => item?.postalCode ?? item?.PostalCode ?? "";
  const getCountry = (item) => item?.country ?? item?.Country ?? "";

  const fetchOrganizations = async () => {
    setLoading(true);
    setError(null);

    try {
      const endpoint = appSettings?.endpoints?.getAllOrganizations;
      if (!endpoint) throw new Error("Get organizations endpoint is not configured.");

      const url = `${appSettings.baseUrl}${endpoint}?_ts=${Date.now()}`;
      const response = await fetchWithAuth(url, { method: "GET", cache: "no-store" });
      if (!response.ok) throw new Error("Failed to load organizations.");

      const data = await response.json();
      setOrganizations(normalizeArray(data));
    } catch (e) {
      setError(e.message || "Failed to load organizations.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const validateRequired = (item) => {
    if (!item.name.trim()) return "Name is required.";
    if (!item.description.trim()) return "Description is required.";
    if (!item.email.trim()) return "Email is required.";
    if (!item.phoneNumber.trim()) return "Phone number is required.";
    if (!item.address.trim()) return "Address is required.";
    return "";
  };

  const buildOrgQuery = (item) => {
    const params = new URLSearchParams({
      id: String(item.id || 0),
      name: item.name.trim(),
      description: item.description.trim(),
      email: item.email.trim(),
      phoneNumber: item.phoneNumber.trim(),
      address: item.address.trim(),
      city: (item.city || "").trim(),
      region: (item.region || "").trim(),
      postalCode: (item.postalCode || "").trim(),
      country: (item.country || "").trim(),
      isActive: item.isActive ? "1" : "0",
    });
    return params.toString();
  };

  const handleOpenCreate = () => {
    setCreateItem({ ...defaultOrg, isActive: true });
    setOpenCreate(true);
  };

  const handleCloseCreate = () => {
    if (saving) return;
    setOpenCreate(false);
  };

  const handleOpenEdit = (item) => {
    setEditItem({
      id: getId(item),
      name: getName(item),
      description: getDescription(item),
      email: getEmail(item),
      phoneNumber: getPhoneNumber(item),
      address: getAddress(item),
      city: getCity(item),
      region: getRegion(item),
      postalCode: getPostalCode(item),
      country: getCountry(item),
      isActive: parseBool(item?.isActive ?? item?.IsActive),
    });
    setOpenEdit(true);
  };

  const handleCloseEdit = () => {
    if (saving) return;
    setOpenEdit(false);
  };

  const handleChangeCreate = (e) => {
    const { name, value } = e.target;
    setCreateItem((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeEdit = (e) => {
    const { name, value } = e.target;
    setEditItem((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveCreate = async () => {
    const validationError = validateRequired(createItem);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const endpoint = appSettings?.endpoints?.createOrganization;
      if (!endpoint) throw new Error("Create organization endpoint is not configured.");

      const url = `${appSettings.baseUrl}${endpoint}?${buildOrgQuery(createItem)}`;
      const response = await fetchWithAuth(url, { method: "POST" });

      if (!response.ok) {
        let errorMessage = "Failed to create organization.";
        try {
          const errorData = await response.json();
          errorMessage = errorData?.title || errorData?.message || errorData?.error || errorMessage;
        } catch {
          // Ignore JSON parse errors.
        }
        throw new Error(errorMessage);
      }

      setOpenCreate(false);
      await fetchOrganizations();
    } catch (e) {
      setError(e.message || "Failed to create organization.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    const validationError = validateRequired(editItem);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const endpoint = appSettings?.endpoints?.updateOrganization;
      if (!endpoint) throw new Error("Update organization endpoint is not configured.");

      const url = `${appSettings.baseUrl}${endpoint}?${buildOrgQuery(editItem)}`;
      const response = await fetchWithAuth(url, { method: "POST" });

      if (!response.ok) {
        let errorMessage = "Failed to update organization.";
        try {
          const errorData = await response.json();
          errorMessage = errorData?.title || errorData?.message || errorData?.error || errorMessage;
        } catch {
          // Ignore JSON parse errors.
        }
        throw new Error(errorMessage);
      }

      setOpenEdit(false);
      await fetchOrganizations();
    } catch (e) {
      setError(e.message || "Failed to update organization.");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenStatusConfirm = (item) => {
    const id = getId(item);
    if (!id) {
      setError("Organization id is missing.");
      return;
    }

    const latest = organizations.find((x) => String(getId(x)) === String(id)) || item;
    setSelectedStatusOrg(latest);
    setOpenStatusConfirm(true);
  };

  const handleCloseStatusConfirm = () => {
    if (statusUpdatingId !== null) return;
    setOpenStatusConfirm(false);
    setSelectedStatusOrg(null);
  };

  const handleToggleStatus = async (item) => {
    const id = getId(item);
    if (!id) {
      setError("Organization id is missing.");
      return;
    }

    const current = parseBool(item?.isActive ?? item?.IsActive);
    const payload = {
      id,
      name: getName(item),
      description: getDescription(item),
      email: getEmail(item),
      phoneNumber: getPhoneNumber(item),
      address: getAddress(item),
      city: getCity(item),
      region: getRegion(item),
      postalCode: getPostalCode(item),
      country: getCountry(item),
      isActive: !current,
    };

    setStatusUpdatingId(id);
    setError(null);
    try {
      const endpoint = appSettings?.endpoints?.updateOrganization;
      if (!endpoint) throw new Error("Update organization endpoint is not configured.");

      const url = `${appSettings.baseUrl}${endpoint}?${buildOrgQuery(payload)}`;
      const response = await fetchWithAuth(url, { method: "POST" });

      if (!response.ok) {
        let errorMessage = "Failed to update organization status.";
        try {
          const errorData = await response.json();
          errorMessage = errorData?.title || errorData?.message || errorData?.error || errorMessage;
        } catch {
          // Ignore JSON parse errors.
        }
        throw new Error(errorMessage);
      }

      await fetchOrganizations();
    } catch (e) {
      setError(e.message || "Failed to update organization status.");
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleConfirmToggleStatus = async () => {
    if (!selectedStatusOrg) return;
    await handleToggleStatus(selectedStatusOrg);
    setOpenStatusConfirm(false);
    setSelectedStatusOrg(null);
  };

  const renderOrgFields = (item, onChange) => (
    <MDBox mt={1} display="grid" gap={2}>
      <TextField fullWidth label="Name" name="name" value={item.name} onChange={onChange} />
      <TextField
        fullWidth
        label="Description"
        name="description"
        value={item.description}
        onChange={onChange}
      />
      <TextField fullWidth label="Email" name="email" value={item.email} onChange={onChange} />
      <TextField
        fullWidth
        label="Phone Number"
        name="phoneNumber"
        value={item.phoneNumber}
        onChange={onChange}
      />
      <TextField
        fullWidth
        label="Address"
        name="address"
        value={item.address}
        onChange={onChange}
        multiline
        minRows={2}
      />
      <TextField fullWidth label="City" name="city" value={item.city} onChange={onChange} />
      <TextField fullWidth label="Region" name="region" value={item.region} onChange={onChange} />
      <TextField
        fullWidth
        label="Postal Code"
        name="postalCode"
        value={item.postalCode}
        onChange={onChange}
      />
      <TextField
        fullWidth
        label="Country"
        name="country"
        value={item.country}
        onChange={onChange}
      />
    </MDBox>
  );

  const tableData = useMemo(() => {
    const columns = [
      { Header: "name", accessor: "name", align: "left" },
      { Header: "email", accessor: "email", align: "left" },
      { Header: "phone", accessor: "phone", align: "left" },
      { Header: "location", accessor: "location", align: "left" },
      { Header: "status", accessor: "status", align: "center" },
      { Header: "action", accessor: "action", align: "center" },
    ];

    const rows = organizations.map((item) => {
      const isActive = parseBool(item?.isActive ?? item?.IsActive);
      const id = getId(item);

      return {
        name: (
          <MDBox lineHeight={1} textAlign="left">
            <MDTypography display="block" variant="button" fontWeight="medium">
              {getName(item)}
            </MDTypography>
            <MDTypography variant="caption" color="text">
              {getDescription(item)}
            </MDTypography>
          </MDBox>
        ),
        email: <MDTypography variant="caption">{getEmail(item)}</MDTypography>,
        phone: <MDTypography variant="caption">{getPhoneNumber(item)}</MDTypography>,
        location: (
          <MDTypography variant="caption" color="text">
            {[getCity(item), getCountry(item)].filter(Boolean).join(", ") || "-"}
          </MDTypography>
        ),
        status: (
          <MDBadge
            badgeContent={isActive ? "active" : "inactive"}
            color={isActive ? "success" : "secondary"}
            variant="gradient"
            size="sm"
          />
        ),
        action: (
          <MDBox display="flex" justifyContent="center" gap={1} flexWrap="wrap">
            <MDButton
              variant="text"
              color="info"
              size="small"
              onClick={() => handleOpenEdit(item)}
              disabled={String(statusUpdatingId) === String(id)}
            >
              Edit
            </MDButton>
            <Tooltip title={isActive ? "Deactivate Organization" : "Activate Organization"}>
              <span>
                <IconButton
                  color={isActive ? "error" : "success"}
                  size="small"
                  onClick={() => handleOpenStatusConfirm(item)}
                  disabled={String(statusUpdatingId) === String(id)}
                >
                  <Icon fontSize="small">{isActive ? "toggle_off" : "toggle_on"}</Icon>
                </IconButton>
              </span>
            </Tooltip>
          </MDBox>
        ),
      };
    });

    return { columns, rows };
  }, [organizations, statusUpdatingId]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="success"
                borderRadius="lg"
                coloredShadow="success"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Organizations
                </MDTypography>
                <MDBox display="flex" gap={1}>
                  <MDButton variant="gradient" color="success" onClick={handleOpenCreate}>
                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                    &nbsp;add organization
                  </MDButton>
                  <MDButton variant="gradient" color="success" onClick={fetchOrganizations}>
                    <Icon sx={{ fontWeight: "bold" }}>refresh</Icon>
                    &nbsp;refresh
                  </MDButton>
                </MDBox>
              </MDBox>

              <MDBox pt={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" alignItems="center" py={10}>
                    <CircularProgress color="success" />
                  </MDBox>
                ) : (
                  <>
                    {error && (
                      <MDBox
                        mx={2}
                        mb={2}
                        py={1}
                        px={2}
                        variant="gradient"
                        sx={{ backgroundColor: "lightcoral" }}
                        borderRadius="lg"
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <MDTypography variant="button" color="white" fontWeight="medium">
                          {error}
                        </MDTypography>
                        <MDTypography
                          color="white"
                          sx={{ cursor: "pointer", ml: 2, lineHeight: 0 }}
                          onClick={() => setError(null)}
                        >
                          x
                        </MDTypography>
                      </MDBox>
                    )}

                    <DataTable
                      table={tableData}
                      isSorted
                      entriesPerPage={{ defaultValue: 10, entries: [5, 10, 25, 50] }}
                      showTotalEntries
                      noEndBorder
                    />
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog open={openCreate} onClose={handleCloseCreate} fullWidth maxWidth="sm">
        <DialogTitle>Add Organization</DialogTitle>
        <DialogContent>{renderOrgFields(createItem, handleChangeCreate)}</DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseCreate} color="secondary" disabled={saving}>
            Cancel
          </MDButton>
          <MDButton onClick={handleSaveCreate} color="info" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openEdit} onClose={handleCloseEdit} fullWidth maxWidth="sm">
        <DialogTitle>Edit Organization</DialogTitle>
        <DialogContent>{renderOrgFields(editItem, handleChangeEdit)}</DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseEdit} color="secondary" disabled={saving}>
            Cancel
          </MDButton>
          <MDButton onClick={handleSaveEdit} color="info" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openStatusConfirm} onClose={handleCloseStatusConfirm} fullWidth maxWidth="xs">
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          <MDTypography variant="button" color="text">
            Are you sure you want to{" "}
            {parseBool(selectedStatusOrg?.isActive ?? selectedStatusOrg?.IsActive)
              ? "deactivate"
              : "activate"}{" "}
            this organization?
          </MDTypography>
          {selectedStatusOrg && (
            <MDBox mt={1}>
              <MDTypography variant="caption" color="text" fontWeight="medium">
                {getName(selectedStatusOrg) || "-"}
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton
            onClick={handleCloseStatusConfirm}
            color="secondary"
            disabled={statusUpdatingId !== null}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleConfirmToggleStatus}
            variant="gradient"
            color={
              parseBool(selectedStatusOrg?.isActive ?? selectedStatusOrg?.IsActive)
                ? "error"
                : "success"
            }
            disabled={statusUpdatingId !== null}
          >
            Confirm
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default Organization;
